import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, Injectable, Input, OnDestroy, TemplateRef, inject } from '@angular/core';

import { ContactInfoCardComponent } from '../contact-info-card/contact-info-card.component';
import { CardDataType } from '../contact-info-types';
import { cardPositions } from './positions';

@Directive({
  selector: '[glxyContactCard]',
  standalone: true,
})
export class ContactInfoOnHoverDirective implements OnDestroy {
  @Input('glxyContactCard') cardData?: CardDataType;

  @Input('glxyContactCardShowDelay') showDelay = 500;

  @Input('glxyContactCardActionsRef') actionsRef?: TemplateRef<unknown>;

  private overlay = inject(Overlay);
  private stateService = inject(ContactCardStateService);
  private elementRef = inject(ElementRef);

  contactCardComponent = new ComponentPortal(ContactInfoCardComponent);
  currentOverlayComponent: ContactInfoCardComponent | null = null;
  contactCardOverlayRef?: OverlayRef;

  triggerIsHovered = false;

  mouseX?: number;
  mouseY?: number;

  openTimer = 0; // the id for our window.setTimeout used to open the popover
  closeTimer = 0; // the id for our window.setTimeout used to open the popover

  @HostListener('mousemove', ['$event']) onMouseMove(event: MouseEvent): void {
    this.mouseX = event.pageX;
    this.mouseY = event.pageY;
  }

  @HostListener('mouseenter') onMouseEnter(): void {
    this.triggerIsHovered = true;
    if (!this.currentOverlayComponent) this.startOpenTimer();
  }

  @HostListener('mouseleave') onMouseLeave(): void {
    this.triggerIsHovered = false;
    window.clearTimeout(this.openTimer);
    this.startCloseTimer();
  }

  @HostListener('window:keydown.escape') onEscapeKey() {
    this.close();
  }

  startOpenTimer() {
    this.openTimer = window.setTimeout(() => {
      this.open();
    }, this.showDelay);
  }

  startCloseTimer() {
    window.clearInterval(this.closeTimer); // close any preexisting timers first
    this.closeTimer = window.setInterval(() => {
      this.checkIfCanClose();
    }, 1000);
  }

  checkIfCanClose() {
    if (!this.triggerIsHovered && !this.currentOverlayComponent?.popoverIsHovered) {
      this.close();
    }
  }

  open() {
    if (!this.cardData) {
      console.warn('appContactCard: No userdata provided!');
      return;
    }
    this.stateService.currentPopover?.close();

    // inspired by https://github.com/scttcper/ngx-rightclick/blob/master/src/lib/context-menu.service.ts

    let tempElement;

    // If the mouse position is not set, use the elementRef
    if (!this.mouseX && !this.mouseY) {
      tempElement = this.elementRef.nativeElement.getBoundingClientRect();
    } else {
      // otherwise, use the mouse position
      tempElement = new ElementRef({
        getBoundingClientRect: () => new DOMRect(this.mouseX, this.mouseY, 0, 0),
      });
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(tempElement)
      .withFlexibleDimensions(false)
      .withPositions(cardPositions);

    const config = new OverlayConfig({
      positionStrategy: positionStrategy,
    });

    this.contactCardOverlayRef = this.overlay.create(config);
    this.currentOverlayComponent = this.contactCardOverlayRef.attach(this.contactCardComponent).instance;
    this.currentOverlayComponent.cardIsPopover = true;
    this.currentOverlayComponent.cardData = this.cardData;
    if (this.actionsRef) {
      this.currentOverlayComponent.actionsRef = this.actionsRef;
    }
    this.stateService.currentPopover = this;
  }

  close() {
    window.clearInterval(this.closeTimer);
    if (!this.currentOverlayComponent) return;
    this.contactCardOverlayRef?.dispose();
    this.currentOverlayComponent = null;
  }

  ngOnDestroy() {
    this.close();
  }
}

//
//

@Injectable({
  providedIn: 'root',
})
export class ContactCardStateService {
  public currentPopover: ContactInfoOnHoverDirective | null = null;
}
